/*
 * Utility module
 */

/**
 * @deprecated
 * @type {{extend: (function(Object, ...[Object]): Object), bind: (function(Function, Object, *=): function(...[*]): any), trim: (function(string): string), removeClass: lib.removeClass, hasClass: (function(HTMLElement, string): *), event: {add: lib.event.add, remove: lib.event.remove}, each: lib.each, addClass: lib.addClass, queryElementsBySelector: ((function(string, (HTMLElement|Document)=): (NodeList|Array))|*)}}
 */
const lib = {
	/**
	 * Checks if the specified element has the given class.
	 *
	 * @param {HTMLElement} el - The element to check.
	 * @param {string} cls - The class name to look for.
	 * @returns {boolean} - Returns true if the element has the class, otherwise false.
	 */
	hasClass: function (el, cls) {
		return el instanceof HTMLElement && el.classList.contains(cls);
	},
	/**
	 * Adds the specified class to the element if it does not already have it.
	 *
	 * @param {HTMLElement} el - The element to which the class will be added.
	 * @param {string} cls - The class name to add.
	 */
	addClass: function (el, cls) {
		if (el instanceof HTMLElement) el.classList.add(cls);
	},
	/**
	 * Removes the specified class from the element if it has it.
	 *
	 * @param {HTMLElement} el - The element from which the class will be removed.
	 * @param {string} cls - The class name to remove.
	 */
	removeClass: function (el, cls) {
		if (el instanceof HTMLElement) el.classList.remove(cls);
	},
	/**
	 * Extends the target object with properties from one or more source objects.
	 *
	 * @param {Object} obj - The target object to extend.
	 * @param {...Object} sources - One or more source objects containing properties to copy.
	 * @returns {Object} - The extended target object.
	 */
	extend: function (obj, ...sources) {
		sources.forEach((source) => {
			Object.keys(source).forEach((key) => {
				if (source.hasOwnProperty(key)) {
					obj[key] = source[key];
				}
			});
		});
		return obj;
	},
	each: function (obj, callback) {
		if (Array.isArray(obj)) {
			for (let i = 0; i < obj.length; i++) {
				if (callback.call(obj[i], i, obj[i]) === false) {
					break;
				}
			}
		} else {
			for (const property in obj) {
				if (obj.hasOwnProperty(property)) {
					if (callback.call(obj[property], property, obj[property]) === false) {
						break;
					}
				}
			}
		}
	},
	event: (function () {
		/**
		 * Fixes the event object to ensure cross-browser compatibility.
		 *
		 * @param {Event} e - The event object to fix.
		 * @returns {Event} - The fixed event object.
		 */
		const fixEvent = function (e = window.event) {
			if (e.isFixed) return e;
			e.isFixed = true;
			e.target = e.target || e.srcElement;
			e.preventDefault =
				e.preventDefault ||
				function () {
					this.returnValue = false;
				};
			e.stopPropagation =
				e.stopPropagation ||
				function () {
					this.cancelBubble = true;
				};
			return e;
		};

		return {
			add: function (elem, event, handler) {
				if (!elem.events) {
					elem.events = {};
					elem.handle = function (e) {
						e = fixEvent(e);
						elem.events[e.type].forEach((handler) => {
							const ret = handler.call(elem, e);
							if (ret === false) {
								e.preventDefault();
								e.stopPropagation();
							}
						});
					};
				}
				if (!elem.events[event]) {
					elem.events[event] = [];
					if (elem.addEventListener)
						elem.addEventListener(event, elem.handle, false);
					else if (elem.attachEvent)
						elem.attachEvent('on' + event, elem.handle);
				}
				elem.events[event].push(handler);
			},
			/**
			 * Removes the specified event handler from the element.
			 *
			 * @param {HTMLElement} elem - The element from which the event handler will be removed.
			 * @param {string} event - The event type to remove.
			 * @param {Function} handler - The event handler function to remove.
			 */
			remove: function (elem, event, handler) {
				const handlers = elem.events[event];
				const index = handlers.indexOf(handler);
				if (index !== -1) handlers.splice(index, 1);
				if (!handlers.length) {
					delete elem.events[event];
					if (elem.removeEventListener)
						elem.removeEventListener(event, elem.handle, false);
					else if (elem.detachEvent)
						elem.detachEvent('on' + event, elem.handle);
				}
			},
		};
	})(),

	/**
	 * Queries elements by a CSS selector within a specified scope.
	 *
	 * @param {string} selector - The CSS selector to query elements.
	 * @param {HTMLElement|Document} [scope=document] - The scope within which to query elements.
	 * @returns {NodeList|Array} - The list of elements matching the selector.
	 */
	queryElementsBySelector: function (selector, scope = document) {
		if (!selector) return [];
		if (selector === '>*') return scope.children;
		if (typeof document.querySelectorAll === 'function') {
			return scope.querySelectorAll(selector);
		}

		const selectors = selector.split(',');

		let resultList = [];

		selectors.forEach((sel) => {
			let currentContext = [scope];
			const tokens = sel.trim().split(' ');

			tokens.forEach((token) => {
				token = token.trim();

				if (token.includes('#')) {
					const [tagName, id] = token.split('#');
					const element = document.getElementById(id);
					if (
						element &&
						tagName &&
						element.nodeName.toLowerCase() !== tagName
					) {
						return [];
					}
					currentContext = element ? [element] : [];
					return;
				}

				if (token.includes('.')) {
					const [tagName = '*', className] = token.split('.');
					let found = [];
					currentContext.forEach((ctx) => {
						const elements = Array.from(ctx.getElementsByTagName(tagName));
						found = [...found, ...elements];
					});
					currentContext = found.filter((el) =>
						el.classList.contains(className)
					);
					return;
				}

				const attrMatch = token.match(
					/^(\w*)\[(\w+)([=~|^$*]?)="?([^\]"]*)"?\]$/
				);
				if (attrMatch) {
					const [_, tagName = '*', attrName, attrOperator, attrValue] =
						attrMatch;
					let found = [];
					currentContext.forEach((ctx) => {
						const elements = Array.from(ctx.getElementsByTagName(tagName));
						found = [...found, ...elements];
					});

					const checkFunction = (el) => {
						const attr = el.getAttribute(attrName);
						switch (attrOperator) {
							case '=':
								return attr === attrValue;
							case '~':
								return attr && attr.split(' ').includes(attrValue);
							case '|':
								return attr && attr.startsWith(attrValue + '-');
							case '^':
								return attr && attr.startsWith(attrValue);
							case '$':
								return attr && attr.endsWith(attrValue);
							case '*':
								return attr && attr.includes(attrValue);
							default:
								return attr !== null;
						}
					};

					currentContext = found.filter(checkFunction);
					return;
				}

				let found = [];
				currentContext.forEach((ctx) => {
					const elements = Array.from(ctx.getElementsByTagName(token));
					found = [...found, ...elements];
				});
				currentContext = found;
			});

			resultList = [...resultList, ...currentContext];
		});

		return resultList;
	},
	/**
	 * Trims leading and trailing whitespace from the given string.
	 *
	 * @param {string} str - The string to trim.
	 * @returns {string} - The trimmed string.
	 */
	trim: function (str) {
		return str.replace(/^\s+/, '').replace(/\s+$/, '');
	},
	/**
	 * Binds a function to a specific scope and optionally forces arguments.
	 *
	 * @param {Function} f - The function to bind.
	 * @param {Object} scope - The scope to bind the function to.
	 * @param {*} [forceArgs] - Optional arguments to force when the function is called.
	 * @returns {Function} - The bound function.
	 */
	bind: function (f, scope, forceArgs) {
		return function (...args) {
			return f.apply(scope, forceArgs !== undefined ? [forceArgs] : args);
		};
	},
};

export default lib;
